import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./render.css";

// =========================================================
// /render?asset=<id>&size=<wxh>
// Pure, padding-less canvases used by Playwright to export
// downloadable brand assets (posts, stories, fb cover, cotización).
// Hidden from public — not linked from nav.
// =========================================================

const LOGO = "/favicon.png";

export default function Render() {
  const [params] = useSearchParams();
  const asset = params.get("asset") || "post-stat";
  const sizeParam = params.get("size");

  // Set page background black so screenshots have no white halo
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "#050505";
    document.documentElement.style.background = "#050505";
    return () => { document.body.style.background = prev; };
  }, []);

  const [w, h] = (() => {
    if (sizeParam) return sizeParam.split("x").map(Number);
    return SIZES[asset] || [1080, 1350];
  })();

  const Canvas = ASSETS[asset] || PostStat;

  return (
    <div className="render-canvas" style={{ width: w, height: h }} data-testid={`render-${asset}`}>
      <Canvas w={w} h={h} />
    </div>
  );
}

const SIZES = {
  "post-stat": [1080, 1350],
  "post-quote": [1080, 1350],
  "post-service": [1080, 1350],
  "story-1": [1080, 1920],
  "story-2": [1080, 1920],
  "story-3": [1080, 1920],
  "fb-cover": [1640, 624],
  "cotizacion": [1240, 1754], // A4 @ 150dpi
};

// ------------------------------ POST STAT ------------------------------
function PostStat() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 96, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(60% 50% at 50% 0%, rgba(255,138,0,0.18) 0%, rgba(255,138,0,0) 70%), radial-gradient(40% 40% at 80% 30%, rgba(255,59,31,0.22) 0%, rgba(255,59,31,0) 70%)" }} />
      <div style={{ position: "absolute", top: -120, right: -80, width: 760, height: 760, borderRadius: 9999, background: "radial-gradient(closest-side, rgba(255,59,31,0.45), transparent 70%)", filter: "blur(40px)" }} />

      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16 }}>
        <img src={LOGO} alt="" style={{ width: 72, height: 72 }} />
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 36, letterSpacing: "-0.02em" }}>Daos Ads</div>
      </div>

      <div style={{ position: "relative" }}>
        <div style={{ fontSize: 24, letterSpacing: "0.24em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>El dato</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, fontSize: 360, lineHeight: 0.9, color: "#fff", letterSpacing: "-0.04em", marginTop: 24 }}>
          78<span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>%</span>
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>.</span>
        </div>
        <div style={{ fontSize: 38, color: "#D4D4D8", marginTop: 28, lineHeight: 1.35, maxWidth: 800 }}>
          De los clientes compra a <b style={{ color: "#fff" }}>quien responde primero</b>. ¿Tú en cuánto respondes?
        </div>
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 22, letterSpacing: "0.22em", textTransform: "uppercase", color: "#A1A1AA", fontFamily: "Sora", fontWeight: 600 }}>@daos.ads</div>
        <div style={{ fontSize: 22, color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>daosads.com →</div>
      </div>
    </div>
  );
}

// ------------------------------ POST QUOTE ------------------------------
function PostQuote() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 96, background: "#0A0A0A", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <img src={LOGO} alt="" style={{ width: 72, height: 72 }} />
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 36, letterSpacing: "-0.02em" }}>Daos Ads</div>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#FF8A00", fontSize: 200, lineHeight: 0.7 }}>"</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 78, lineHeight: 1.08, letterSpacing: "-0.02em", marginTop: 16, maxWidth: 880 }}>
            No vendas estrategia bonita.{" "}
            <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>
              Implementa sistemas
            </span>{" "}
            que sigan operando sin ti.
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 56, height: 56, borderRadius: 9999, background: "linear-gradient(135deg,#FF3B1F,#FF8A00,#FFD23F)", display: "grid", placeItems: "center", fontFamily: "Sora", fontWeight: 800, color: "#000", fontSize: 24 }}>D</div>
        <div>
          <div style={{ fontFamily: "Sora", fontWeight: 700, color: "#fff", fontSize: 24 }}>David Osorio</div>
          <div style={{ color: "#A1A1AA", fontSize: 18 }}>Fundador · Daos Ads</div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ POST SERVICE ------------------------------
function PostService() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 96, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 16, background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)" }} />
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(60% 50% at 50% 100%, rgba(255,138,0,0.10) 0%, rgba(255,138,0,0) 70%)" }} />

      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 80 }}>
          <img src={LOGO} alt="" style={{ width: 72, height: 72 }} />
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 36, letterSpacing: "-0.02em" }}>Daos Ads</div>
        </div>
        <div style={{ fontSize: 24, letterSpacing: "0.26em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Servicio</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 130, lineHeight: 0.96, letterSpacing: "-0.04em", marginTop: 28 }}>
          Implementación<br />
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>Respond.io</span>
        </div>
        <div style={{ fontSize: 32, color: "#D4D4D8", marginTop: 32, lineHeight: 1.4, maxWidth: 850 }}>
          Centralizo tus canales, implemento IA y dejo a tu equipo operando enterprise-ready.
        </div>
      </div>

      <div style={{ position: "relative", border: "1px solid rgba(255,138,0,0.28)", background: "rgba(255,255,255,0.03)", backdropFilter: "blur(20px)", borderRadius: 28, padding: "28px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 22, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Escríbeme</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 38, marginTop: 8, letterSpacing: "-0.01em" }}>daosads.com/respond-io</div>
      </div>
    </div>
  );
}

// ------------------------------ STORY 1 ------------------------------
function Story1() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 100, display: "flex", flexDirection: "column" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(60% 50% at 50% 0%, rgba(255,138,0,0.22) 0%, rgba(255,138,0,0) 70%), radial-gradient(50% 40% at 80% 80%, rgba(255,59,31,0.18) 0%, rgba(255,59,31,0) 70%)" }} />
      <div style={{ position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)", width: 1100, height: 1100, borderRadius: 9999, background: "radial-gradient(closest-side, rgba(255,138,0,0.32), transparent 70%)", filter: "blur(40px)" }} />

      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16 }}>
        <img src={LOGO} alt="" style={{ width: 64, height: 64 }} />
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 30, letterSpacing: "-0.02em" }}>Daos Ads</div>
      </div>

      <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 24, letterSpacing: "0.28em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Nuevo</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 130, lineHeight: 1.02, letterSpacing: "-0.035em", marginTop: 32, maxWidth: 880 }}>
            3 errores que te están{" "}
            <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>costando leads</span>.
          </div>
        </div>
      </div>

      <div style={{ position: "relative", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(20px)", borderRadius: 32, padding: "28px 36px", textAlign: "center" }}>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 38 }}>Desliza hacia arriba</div>
        <div style={{ fontSize: 22, letterSpacing: "0.22em", textTransform: "uppercase", color: "#A1A1AA", fontFamily: "Sora", fontWeight: 600, marginTop: 6 }}>daosads.com</div>
      </div>
    </div>
  );
}

// ------------------------------ STORY 2 ------------------------------
function Story2() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 100, background: "#0A0A0A", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={LOGO} alt="" style={{ width: 64, height: 64 }} />
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 30, letterSpacing: "-0.02em" }}>Daos Ads</div>
        </div>
        <div style={{ width: 18, height: 18, borderRadius: 9999, background: "#25D366", boxShadow: "0 0 0 8px rgba(37,211,102,0.25)" }} />
      </div>

      <div style={{ marginTop: 60, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ alignSelf: "flex-start", maxWidth: 720, padding: "22px 26px", borderRadius: 28, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#E4E4E7", fontSize: 32, lineHeight: 1.35 }}>
          Hola, ¿manejan automatización con IA?
        </div>
        <div style={{ alignSelf: "flex-end", maxWidth: 720, padding: "22px 26px", borderRadius: 28, background: "linear-gradient(135deg,#FF3B1F,#FF8A00,#FFD23F)", color: "#0A0A0A", fontSize: 32, lineHeight: 1.35, fontWeight: 600 }}>
          Sí, te agendo demo mañana 10:30 AM.
        </div>
        <div style={{ alignSelf: "flex-start", maxWidth: 720, padding: "22px 26px", borderRadius: 28, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#E4E4E7", fontSize: 32, lineHeight: 1.35 }}>
          Confirmado.
        </div>
      </div>

      <div style={{ marginTop: "auto" }}>
        <div style={{ fontSize: 24, letterSpacing: "0.26em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Así responde tu negocio</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 110, lineHeight: 1.05, letterSpacing: "-0.035em", marginTop: 24 }}>
          24/7. En segundos.<br />
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>Sin tocar nada.</span>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ STORY 3 ------------------------------
function Story3() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 110, background: "linear-gradient(160deg,#050505 0%,#1a0a00 100%)", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 14, background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 14, background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <img src={LOGO} alt="" style={{ width: 64, height: 64 }} />
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 30, letterSpacing: "-0.02em" }}>Daos Ads</div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 24, letterSpacing: "0.28em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Consultoría</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 170, lineHeight: 0.96, letterSpacing: "-0.045em", marginTop: 32 }}>
          Gana más,<br />
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>pierde cero.</span>
        </div>
        <div style={{ marginTop: 32, color: "#D4D4D8", fontSize: 30 }}>Diagnóstico sin compromiso.</div>
      </div>

      <div style={{ textAlign: "center", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", backdropFilter: "blur(20px)", borderRadius: 32, padding: "32px" }}>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 42 }}>Agenda ahora →</div>
      </div>
    </div>
  );
}

// ------------------------------ FB COVER ------------------------------
function FbCover() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: "60px 90px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(60% 70% at 50% 0%, rgba(255,138,0,0.15) 0%, rgba(255,138,0,0) 70%)" }} />
      <div style={{ position: "absolute", top: -200, right: -100, width: 1100, height: 1100, borderRadius: 9999, background: "radial-gradient(closest-side, rgba(255,59,31,0.30), transparent 70%)", filter: "blur(50px)" }} />

      <div style={{ position: "relative", maxWidth: "62%" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 14, fontSize: 18, letterSpacing: "0.24em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>
          <span style={{ width: 36, height: 1, background: "linear-gradient(90deg,transparent,#FF8A00)" }} />
          Consultor · Operador · Implementador
        </div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 78, lineHeight: 1.04, letterSpacing: "-0.035em", marginTop: 24 }}>
          Automatiza tu negocio,<br />
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>escala tus ventas</span> con IA.
        </div>
        <div style={{ marginTop: 22, color: "#A1A1AA", fontSize: 22 }}>
          Respond.io · Google Ads · Meta Ads · TikTok Ads · Funnels
        </div>
      </div>

      <div style={{ position: "relative", flexShrink: 0 }}>
        <img src={LOGO} alt="" style={{ width: 280, height: 280, filter: "drop-shadow(0 12px 60px rgba(255,138,0,0.5))" }} />
      </div>

      <div style={{ position: "absolute", bottom: 32, left: 90, fontSize: 18, letterSpacing: "0.26em", textTransform: "uppercase", color: "#71717A", fontFamily: "Sora", fontWeight: 600 }}>
        daosads.com
      </div>
    </div>
  );
}

// ------------------------------ COTIZACION (A4 150dpi = 1240×1754) ------------------------------
function Cotizacion() {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", padding: 80, background: "#050505", display: "flex", flexDirection: "column" }}>
      <div style={{ position: "absolute", top: -150, right: -100, width: 700, height: 700, borderRadius: 9999, background: "radial-gradient(closest-side, rgba(255,59,31,0.18), transparent 70%)", filter: "blur(40px)" }} />

      {/* Header */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src={LOGO} alt="" style={{ width: 64, height: 64 }} />
          <div>
            <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 28, letterSpacing: "-0.02em" }}>Daos Ads</div>
            <div style={{ fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700, marginTop: 2 }}>Gana más, pierde cero</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#71717A" }}>Propuesta comercial</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 32, marginTop: 4, letterSpacing: "-0.02em" }}>#DAOS-2026-001</div>
          <div style={{ color: "#71717A", fontSize: 14, marginTop: 4 }}>Guatemala · 02 May 2026</div>
        </div>
      </div>

      {/* Client */}
      <div style={{ marginTop: 50, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36, position: "relative" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Preparada para</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 22, marginTop: 6 }}>Nombre Cliente S.A.</div>
          <div style={{ color: "#A1A1AA", fontSize: 16, marginTop: 2 }}>Director Comercial</div>
          <div style={{ color: "#71717A", fontSize: 13, marginTop: 4 }}>contacto@cliente.com</div>
        </div>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Preparada por</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 22, marginTop: 6 }}>David Osorio</div>
          <div style={{ color: "#A1A1AA", fontSize: 16, marginTop: 2 }}>Fundador · Daos Ads</div>
          <div style={{ color: "#71717A", fontSize: 13, marginTop: 4 }}>david.osorio@daosads.com</div>
        </div>
      </div>

      <div style={{ marginTop: 40, height: 1, background: "linear-gradient(90deg,transparent,rgba(255,138,0,0.4),transparent)" }} />

      {/* Scope */}
      <div style={{ marginTop: 40, position: "relative", flex: 1 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF8A00", fontFamily: "Sora", fontWeight: 700 }}>Alcance del proyecto</div>
        <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 44, lineHeight: 1.06, letterSpacing: "-0.025em", marginTop: 12 }}>
          Implementación omnicanal con IA +<br />
          <span style={{ background: "linear-gradient(90deg,#FF3B1F,#FF8A00,#FFD23F)", WebkitBackgroundClip: "text", color: "transparent" }}>adquisición paga conectada.</span>
        </div>

        <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            ["01", "Auditoría operativa y plan de trabajo de 30 días."],
            ["02", "Implementación de Respond.io + IA conversacional."],
            ["03", "Configuración de campañas Google Ads & Meta Ads."],
            ["04", "Integración de CRM, tracking server-side y reportes."],
            ["05", "Entrenamiento del equipo comercial."],
          ].map(([n, t]) => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14 }}>
              <span style={{ color: "#FF8A00", fontFamily: "Sora", fontWeight: 800, fontSize: 16, width: 32 }}>{n}</span>
              <span style={{ color: "#E4E4E7", fontSize: 16 }}>{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Total */}
      <div style={{ marginTop: 32, position: "relative", border: "1px solid rgba(255,138,0,0.3)", background: "rgba(255,255,255,0.03)", borderRadius: 18, padding: "28px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#71717A" }}>Inversión total</div>
          <div style={{ fontFamily: "Sora", fontWeight: 800, color: "#fff", fontSize: 50, marginTop: 4, letterSpacing: "-0.03em" }}>
            $ 0,000 <span style={{ color: "#71717A", fontSize: 22, fontWeight: 400 }}>USD</span>
          </div>
          <div style={{ color: "#71717A", fontSize: 12, marginTop: 4 }}>Más impuestos aplicables · válida 15 días</div>
        </div>
        <div style={{ background: "linear-gradient(135deg,#FF3B1F,#FF8A00,#FFD23F)", color: "#0A0A0A", padding: "16px 28px", borderRadius: 9999, fontFamily: "Sora", fontWeight: 700, fontSize: 16 }}>Aceptar propuesta →</div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: "#52525B" }}>
        <div>daosads.com · +502 4337 6954 · david.osorio@daosads.com</div>
        <div style={{ letterSpacing: "0.24em", textTransform: "uppercase" }}>Confidencial · uso exclusivo cliente</div>
      </div>
    </div>
  );
}

const ASSETS = {
  "post-stat": PostStat,
  "post-quote": PostQuote,
  "post-service": PostService,
  "story-1": Story1,
  "story-2": Story2,
  "story-3": Story3,
  "fb-cover": FbCover,
  "cotizacion": Cotizacion,
};
